/**
 * CookieAuth - Core cookie operations (auth, refresh, org, remember-me)
 */

import {
  AUTH_COOKIE_NAME, REFRESH_COOKIE_NAME, ORG_COOKIE_NAME,
  REMEMBER_ME_COOKIE_NAME, AUTH_DOMAIN, IS_SECURE,
} from './constants'

function cookieBase(): { domain: string; secure: string } {
  return {
    domain: AUTH_DOMAIN ? `domain=${AUTH_DOMAIN};` : '',
    secure: IS_SECURE ? 'secure;' : '',
  }
}

function setCookie(name: string, value: string, expiresInDays: number): void {
  if (typeof document === 'undefined') return
  const expires = new Date(Date.now() + expiresInDays * 86_400_000)
  const { domain, secure } = cookieBase()
  document.cookie = `${name}=${encodeURIComponent(value)}; ${domain} path=/; expires=${expires.toUTCString()}; ${secure} samesite=lax`
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function clearCookie(name: string): void {
  if (typeof document === 'undefined') return
  const { domain } = cookieBase()
  document.cookie = `${name}=; ${domain} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

// AUTH TOKEN
export function setAuthCookie(token: string, expiresInDays = 7): void { setCookie(AUTH_COOKIE_NAME, token, expiresInDays) }
export function getAuthCookie(): string | null { return getCookie(AUTH_COOKIE_NAME) }
export function clearAuthCookie(): void { clearCookie(AUTH_COOKIE_NAME) }

// REFRESH TOKEN
export function setRefreshCookie(token: string, expiresInDays = 30): void { setCookie(REFRESH_COOKIE_NAME, token, expiresInDays) }
export function getRefreshCookie(): string | null { return getCookie(REFRESH_COOKIE_NAME) }
export function clearRefreshCookie(): void { clearCookie(REFRESH_COOKIE_NAME) }

// ORGANISATION
export function setOrgCookie(orgId: string, orgName?: string): void {
  setCookie(ORG_COOKIE_NAME, JSON.stringify({ id: orgId, name: orgName || '' }), 30)
}
export function getOrgCookie(): { id: string; name: string } | null {
  const raw = getCookie(ORG_COOKIE_NAME)
  if (!raw) return null
  try { return JSON.parse(raw) } catch { return null }
}
export function clearOrgCookie(): void { clearCookie(ORG_COOKIE_NAME) }

// REMEMBER ME
export function setRememberMeCookie(rememberMe: boolean): void {
  setCookie(REMEMBER_ME_COOKIE_NAME, rememberMe ? '1' : '0', 365)
}
export function getRememberMeCookie(): boolean {
  const val = getCookie(REMEMBER_ME_COOKIE_NAME)
  const result = val === '1'
  if (typeof window !== 'undefined' && window.location.hostname.includes('quotech.ai')) {
    console.log(`[@quotech/auth] getRememberMeCookie: match=${val}, result=${result}`)
  }
  return result
}
export function clearRememberMeCookie(): void { clearCookie(REMEMBER_ME_COOKIE_NAME) }
